import { createApp, nextTick } from 'vue'
import axios from 'axios'
import QuoteRequestForm from './components/QuoteRequestForm'
import productDto from './product-dto'
import { tns } from 'tiny-slider/src/tiny-slider'

const app = createApp({
  mixins: [productDto],
  data() {
    return {
      isLoading: false,
      filters: {},
      curAttributeGroup: null,
      productCategory: window.arno.productCategory || {},
      attributeFilters: [],
      attributeGroups: [],
      attributeLegend: [],
      attributeMedia: [],
      products: [],
      geometries: [],
      geometryImages: window.arno.geometryImages || {},
      highlightedProduct: null,
      quickLook: {
        product: null,
        isVisible: false,
        quoteRequestForm: {
          isVisible: false,
          isSuccessful: false,
          quantity: 1,
          key: 1, // Used to force the QuoteRequestForm component to refresh.
        },
      },
    }
  },
  components: {
    QuoteRequestForm
  },
  computed: {
    filteredProducts() {
      if (! Object.keys(this.filters).length) {
        return this.products
      }

      return this.products.filter(product => {
        for (let [filter, value] of Object.entries(this.filters)) {
          if (value && value.length > 0 && product.attributes[filter] != value) {
            return false
          }
        }
        return true
      });
    },
    attributes() {
      let r = [];
       for (let i = 0; i < this.attributeGroups.length; i++) {
        for (let j = 0; j < this.attributeGroups[i].attributes.length; j++) {
          r.push({
            group: this.attributeGroups[i].name,
            attribute: this.attributeGroups[i].attributes[j]
          })
        }
      }
      return r
    },
    /**
     * Get media for the current filtered attribute selections.
     */
    media() {
      let media = [], curMedia, attribute, numAttributes, numMatches;

      if (! Object.keys(this.attributeMedia).length || ! Object.keys(this.filters).length) {
        return null
      }

      // Media
      for (let i = 0; i < this.attributeMedia.length; i++) {
        curMedia = this.attributeMedia[i]
        numAttributes = curMedia.attributes.length
        numMatches = 0

        // Media attributes
        for (var j = 0; j < numAttributes; j++) {
          attribute = this.attributeMedia[i].attributes[j]

          // Filters
          for (var [filter, value] of Object.entries(this.filters)) {
            filter = filter.trim()
            value = value ? value.toString().trim() : value

            if (
              (attribute.attribute == filter && attribute.value == value) ||
              (attribute.attribute == '*' && attribute.value == '*') ||
              (attribute.attribute == '*' && attribute.value == value) ||
              (attribute.attribute == filter && attribute.value == '*')
            ) {
              numMatches++
            }
          }
        }

        if (numMatches >= numAttributes) {
          media.push(curMedia)
        }
      }

      return media
    },
    /**
     * Get the image paths for the current filtered attribute selections.
     */
    attributeImages() {
      if (this.media) {
        let images = []

        for (let i = 0; i < this.media.length; i++) {
          if (this.media[i].image) {

            // File path...
            if (! Array.isArray(this.media[i].image)) {
              images.push(this.media[i].image)

            //... or an array of file paths.
            } else {
              for (let j = 0; j < this.media[i].image.length; j++) {
                images.push(this.media[i].image[j])
              }
            }
          }
        }

        return images
      }
    },
    /**
     * Get the videos for the current filtered attribute selections.
     */
    attributeVideos() {
      if (this.media) {
        let videos = []

        for (let i = 0; i < this.media.length; i++) {
          if (this.media[i].video) {
            videos.push(this.media[i].video)
          }
        }

        return videos
      }
    },
    /**
     * Get the spec sheet paths for the current filtered attribute selections.
     */
    attributeSpecSheets() {
      if (this.media) {
        let specSheets = []

        for (let i = 0; i < this.media.length; i++) {
          if (
            this.media[i].spec_sheet && 
            Object.values(this.media[i].spec_sheet).length
          ) {

            // Image path...
            if (! Array.isArray(this.media[i].spec_sheet)) {
              specSheets.push(this.media[i].spec_sheet)

            //... or an array of image paths.
            } else {
              for (let j = 0; j < this.media[i].spec_sheet.length; j++) {
                specSheets.push(this.media[i].spec_sheet[j])
              }
            }
          }
        }

        return specSheets
      }
    },
    /**
     * Prepare an object to be used when sending a Quick Look 
     * quote request.
     */
    quickLookProductForQuoteRequest() {
      if (! this.quickLook.product) {
        return null
      }

      return this.getProductDto(
        this.productCategory.title, 
        this.quickLook.product, 
        this.quickLook.quoteRequestForm.quantity
      )
    },
    /**
     * Get the geometry for the current Quick Look product.
     */
    quickLookGeometry() {
      if (! this.quickLook.product) {
        return null
      }

      return this.getGeometry(this.quickLook.product.attributes['Geometry'])
    }
  },
  async mounted() {
    this.isLoading = true

    // Load the product category data.
    const res = await axios.get('/api/product-category/' + this.productCategory.slug)

    this.attributeFilters = res.data.attributeFilters
    this.attributeGroups = res.data.attributeGroups
    this.attributeLegend = res.data.attributeLegend
    this.attributeMedia = res.data.attributeMedia
    this.products = res.data.products
    this.geometries = res.data.geometries

    // Set the current attribute group.
    if (this.attributeGroups.length > 1) {
      this.curAttributeGroup = this.attributeGroups[1].name
    }

    // Highlight a specific product.
    nextTick(() => {
      const queryString = window.location.search
      const urlParams = new URLSearchParams(queryString)
      let urlProductEdp, urlProductRow, productScrollToTop, headerHeight, extraPadding = 0

      if (urlProductEdp = urlParams.get('product')) {

        // Set the filters to the product's specs.
        const urlProduct = this.products.find(product => product.attributes.EDP == urlProductEdp)
        if (urlProduct) {
          for (const attributeFilter in this.attributeFilters) {
            this.filters[this.attributeFilters[attributeFilter]] = urlProduct.attributes[this.attributeFilters[attributeFilter]]
          }
        }

        // Find the product's row.
        urlProductRow = document.getElementById('product-' + urlProductEdp)
        if (urlProductRow) {

          // Highlight
          this.highlightedProduct = urlProduct

          // Scroll down
          // productScrollToTop = urlProductRow.getBoundingClientRect().top
          productScrollToTop = document.getElementById('attribute-groups').getBoundingClientRect().top
          headerHeight = document.querySelector('.app-header').offsetHeight

          scroll({
            top: productScrollToTop - headerHeight - extraPadding,
            behavior: 'smooth',
          })
        }
      }
    })

    // Banner slideshow
    const productCategorySlider = tns({
      container: '.banner.banner--solution .slides',
      items: 1,
      mode: 'gallery',
      slideBy: 'page',
      autoplay: true,
      autoplayButtonOutput: false,
      controls: false,
      nav: false,
      lazyload: true,
    })

    this.isLoading = false
  },
  methods: {
    setAttrGroup(name) {
      this.curAttributeGroup = name
    },
    getProductAttributes(product) {
      let r = []
      for (let i = 0; i < this.attributes.length; i++) {
        r.push(product.attributes[this.attributes[i]])
      }
      return r
    },
    getAttributeColor(attribute) {
      if (this.attributeLegend) {
        let r = this.attributeLegend.filter(function(k) {
          return k.attribute == attribute
        })

        if (r.length) {
          return r[0].color
        }
      }
    },
    filterProducts(filters) {
      let self = this
      if (! filters || ! Object.keys(filters).length) {
        return self.products
      }

      return self.products.filter(function(product) {
        for (let [filter, value] of Object.entries(filters)) {
          if (value && value.length > 0 && product.attributes[filter] != value) {
            return false
          }
        }

        return true
      })
    },
    /**
     * This method returns all product attributes for the given
     * filter, that are among the currently-filtered products.
     */
    getFilterOptions(filter) {

      // Collect the current filters aside from the one provided
      let filters = {}
      for (let prop in this.filters) {
        if (prop != filter) {
          filters[prop] = this.filters[prop]
        }
      }

      // Collect the products filtered by the other current filters
      let products = this.filterProducts(filters)
      let r = []
      for (let i = 0; i < products.length; i++) {
        if (! r.includes(products[i].attributes[filter]) && products[i].attributes[filter] != null) {
          r.push(products[i].attributes[filter])
        }
      }

      // Sort alphabetically
      r = r.sort((a, b) => {
        return a > b ? 1 : -1
      })

      // In the case of inches and metric, place inches first.
      r = r.sort((a, b) => {
        a = a.toString()
        b = b.toString()

        if (a.indexOf('"') > -1 && b.indexOf('"') == -1) {
          return -1
        } else if (a.indexOf('"') == -1 && b.indexOf('"') > -1) {
          return 1
        }
        return 0
      })

      return r
    },
    setFilter(filter, event) {
      this.filters[filter] = event.target.value
    },
    resetFilters() {
      this.filters = {}
    },
    /**
     * Add a product to the toolbox, 
     * or increment the quantity if already added.
     */
    addToToolbox(product) {
      const event = new CustomEvent('addToToolbox', {
        detail: {
          product,
        }
      })
      window.dispatchEvent(event)
    },
    /**
     * Get a geometry by its name/title, (e.g. "S1").
     */
    getGeometry(geometryName) {
      let geometry = this.geometries.find(g => {
        return g.title == geometryName
      })

      if (geometry) {
        geometry.thumbnail = this.geometryImages[geometry.id]
      }

      return geometry
    },
    /**
     * Determine if a product has a Quick Look.
     * It does if its geometry attribute corresponds
     * to an available geometry.
     */
    productHasQuickLook(product) {
      return this.getGeometry(product.attributes['Geometry'])
    },
    /**
     * Show the Quick Look popover.
     * Also resets the Quick Look.
     */
    showQuickLook(product) {
      this.quickLook.product = product
      this.quickLook.isVisible = true

      this.resetQuickLookQuoteRequestForm()
    },
    /**
     * Reset the Quick Look popover quote request form.
     */
    resetQuickLookQuoteRequestForm() {
      this.quickLook.quoteRequestForm.isVisible = false
      this.quickLook.quoteRequestForm.isSuccessful = false
      this.quickLook.quoteRequestForm.key++
    },
    /**
     * Hide the Quick Look popover.
     */
    hideQuickLook() {
      this.quickLook.isVisible = false
    },
    /**
     * Scroll to the product attributes.
     */
    scrollToAttributes() {
      const offsetTop = document.querySelector('#attribute-groups').offsetTop
      const headerHeight = document.querySelector('.app-header').offsetHeight

      scroll({
        top: offsetTop - headerHeight,
        behavior: 'smooth',
      })
    },
  }
})

app.mount('#app')
